import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import type { Query } from "@tanstack/react-query";
import type {
  PersistQueryClientProviderProps,
  PersistedClient,
} from "@tanstack/react-query-persist-client";
import SuperJSON, { type SuperJSONResult } from "superjson";
import { env } from "@/app/config/env";
import { log } from "@/app/observability";
import { isNative, preferences } from "@/app/platform";
import { queryKeys } from "./query-keys";

/**
 * THE CACHE THAT SURVIVES BEING KILLED.
 *
 * iOS reclaims a backgrounded app whenever it wants the memory, so on a phone MOST launches
 * are cold: the process is new, the query cache is empty, and every screen renders its empty
 * state and then blocks on a round trip this project budgets at four seconds. On the web
 * that is a refresh nobody does; on a phone it is what the app looks like most mornings.
 *
 * So a whitelisted slice of the cache is written to disk and hydrated before the first
 * paint. The user sees yesterday's list immediately and it is replaced the moment the
 * refetch lands (`staleTime` is 30s, so that is instantly).
 *
 * WHAT THIS FILE IS NOT: a resume hook. TanStack's focus manager already refetches on
 * `visibilitychange`, and app/platform/app-state.ts reports foreground/background for
 * anything else that needs it. Persistence needs neither, and a third mechanism would make
 * every return from the background fetch twice.
 */

/**
 * WHERE IT IS WRITTEN, AND WHY IT IS NOT THE OBVIOUS PLACE.
 *
 * Three stores were on the table, and on a phone exactly one of them is safe from the system
 * deciding it wants the space back:
 *
 *   - `app/platform/storage.ts` — that seam is the iOS KEYCHAIN. It is for credentials: it
 *     is slow, it is size-constrained, and a list of rows is not a secret in the sense the
 *     Keychain exists to protect. Rejected.
 *   - `localStorage` — inside Capacitor this is the WKWebView's own store, and WebKit is
 *     allowed to EVICT it when the device runs short of space. This file used to write there,
 *     and eviction is the worst shape a failure can have: nothing breaks, nothing is logged,
 *     the app simply goes back to the empty screen and the four-second wait that persistence
 *     exists to remove. "It feels slow again sometimes" is close to unreportable.
 *   - `app/platform/preferences.ts` — on iOS that is `UserDefaults`, which the system does
 *     not reclaim. Chosen, and it is the only property that changed.
 *
 * IT IS NO MORE PRIVATE THAN WHAT IT REPLACES. `UserDefaults` is a plaintext plist inside
 * the app container and it is swept into iCloud and iTunes backups, exactly as the web view
 * store was. Nothing became safe to store that was not safe to store before, and the
 * allow-list below is as load-bearing as it was the day it was written: it — not the choice
 * of store — is the reason nothing user-identifying reaches a phone's disk.
 *
 * The blob still goes when the app is deleted, and on the web it is still `localStorage`
 * because a browser has nothing better. Both degrade to an empty cache on launch, which is
 * the behaviour this feature was added to improve on rather than a new failure.
 *
 * Reversing it is one file: the interface below is three async methods, which is what
 * `createAsyncStoragePersister` wants and what the platform module exposes, so swapping the
 * implementation again touches nothing else.
 */
interface AsyncKeyValueStore {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
}

/** One blob, one key. Namespaced so it cannot collide with the language or session entries. */
export const PERSISTED_CACHE_KEY = "app.query-cache";

/**
 * THE ONLY WAY A LOST CACHE EVER BECOMES VISIBLE.
 *
 * `UserDefaults` is not evicted, so this should never fire — which is exactly why it is
 * worth having. Without it, "the store lost the blob" and "nobody has signed in yet" are the
 * same observation from inside the app: an empty read. This second key says which one it
 * was. It is written beside every save and removed with it, so a read that finds no cache
 * while this is still there means something took the cache and left the marker.
 *
 * It holds only an ISO timestamp — the log line is far more useful for saying how old the
 * copy was — and nothing about the user or what was cached.
 *
 * WHAT IT CANNOT SEE, and this is not fixable from inside the app: if the whole store goes
 * at once (the app is deleted and reinstalled, or a future platform wipes the container)
 * the marker goes with the cache and the launch is indistinguishable from a first run. This
 * catches the cache disappearing, not the store disappearing.
 */
export const CACHE_WRITTEN_AT_KEY = `${PERSISTED_CACHE_KEY}.written-at`;

/**
 * HOW LONG A COLD-START COPY IS ALLOWED TO BE BELIEVED.
 *
 * Also the in-memory `gcTime` — query-client.ts imports this constant rather than choosing
 * its own number, and that coupling is the point. A query dropped from memory is dropped
 * from the next save too, so with the old five-minute `gcTime` the disk copy would have held
 * only whatever screen was open in the last five minutes of the session. The two numbers
 * have to agree, so there is only one of them.
 */
export const PERSISTED_MAX_AGE = 24 * 60 * 60_000;

/**
 * A CEILING, AND THE CACHE IS DROPPED RATHER THAN TRIMMED WHEN IT IS HIT.
 *
 * Half a written cache is worse than none: a truncated blob either fails to parse (and is
 * discarded anyway, one launch later than necessary) or parses into a list that is missing
 * rows nobody can explain. So an oversized payload is not written and the previous one is
 * removed, which returns the app to the empty-on-launch behaviour it has today.
 *
 * 512 KiB is many hundreds of rows of the shape this app stores, and it is a comfortable fit
 * in BOTH stores this writes to: well under WebKit's per-origin allowance on the web, and
 * small enough on a phone that the `UserDefaults` plist — which iOS reads into memory at
 * launch, and which is therefore the wrong place for anything bulky — stays cheap.
 * `persistedCacheBytes()` exists so the real number can be measured rather than guessed at.
 */
export const CACHE_BYTE_BUDGET = 512 * 1024;

function byteLength(value: string): number {
  return new TextEncoder().encode(value).byteLength;
}

/** Once per launch. A cache over budget stays over budget, and one line says it. */
let budgetWarned = false;

/** Once per launch: the first read of the cache key IS the cold-start read. */
let coldStartHandled = false;

/**
 * THE FIRST READ AFTER LAUNCH, which is the only moment either of these two things can be
 * observed. Both cost nothing on the happy path: a cache that is there returns immediately
 * and neither branch runs.
 */
async function onColdStartRead(found: string | null): Promise<void> {
  if (coldStartHandled) return;
  coldStartHandled = true;

  /**
   * A COPY LEFT BEHIND BY THE PREVIOUS IMPLEMENTATION. Until this change the blob was written
   * to the web view's `localStorage`; on device that copy is now unreachable — and, worse,
   * `clearPersistedCache()` no longer reaches it at sign-out, so the previous user's notes
   * would sit in the container for the life of the install. The launch that matters is the
   * first one on a build carrying this; every launch after it removes a key that is not
   * there, which costs nothing and is cheaper than a flag saying it has been done.
   */
  if (isNative) {
    try {
      globalThis.localStorage?.removeItem(PERSISTED_CACHE_KEY);
    } catch {
      /* the web view store is unreadable; there is nothing to clean up either way */
    }
  }

  if (found !== null) return;

  const writtenAt = await preferences.get(CACHE_WRITTEN_AT_KEY);
  if (!writtenAt) return; // nothing was ever written: a first launch, or a sign-out

  // Cleared so one loss produces one line, rather than every launch until the next save.
  await preferences.remove(CACHE_WRITTEN_AT_KEY);
  log.info("persisted query cache was gone on a cold start", { writtenAt });
}

const store: AsyncKeyValueStore = {
  async getItem(key) {
    const value = await preferences.get(key);
    if (key === PERSISTED_CACHE_KEY) await onColdStartRead(value);
    return value;
  },
  async setItem(key, value) {
    const size = byteLength(value);
    if (size > CACHE_BYTE_BUDGET) {
      if (!budgetWarned) {
        budgetWarned = true;
        log.warn("query cache over its disk budget, dropping it", {
          bytes: size,
          budget: CACHE_BYTE_BUDGET,
        });
      }
      await store.removeItem(key);
      return;
    }
    try {
      await preferences.set(key, value);
      if (key === PERSISTED_CACHE_KEY) {
        await preferences.set(CACHE_WRITTEN_AT_KEY, new Date().toISOString());
      }
    } catch {
      // A quota error here is the same outcome as being over budget: no cache, cold start.
      await store.removeItem(key);
    }
  },
  async removeItem(key) {
    try {
      await preferences.remove(key);
      // The marker never outlives the blob: a sign-out that left it behind would make the
      // next launch report a loss that was actually a deliberate erasure.
      if (key === PERSISTED_CACHE_KEY) await preferences.remove(CACHE_WRITTEN_AT_KEY);
    } catch {
      /* nothing left to do — the entry is unreachable either way */
    }
  },
};

/**
 * WHAT MAY BE WRITTEN TO DISK — an allow-list, never a deny-list.
 *
 * A deny-list makes a new endpoint default to LEAKING: someone adds a query, forgets this
 * file, and their data is on disk on every phone the next release reaches. An allow-list
 * makes it default to safe, and the cost is one line here when a screen genuinely needs to
 * survive a cold start.
 *
 * Entries are `domain/action` — segments 0 and 1 of a cache key, the same pair the failure
 * handler in query-client.ts reads to name a report. Every key in the registry is
 * `[domain, action, ...]`, so a detail key and its list share nothing but the domain and
 * each is approved separately.
 *
 * ON THE LIST:
 *   · `notes/list` — the user's own notes, and the entire reason this exists: it is the one
 *     screen a cold start leaves empty. No email, no name, no user id — just the rows the
 *     person who is signed in wrote. Dropped from disk at sign-out (see
 *     `clearPersistedCache`), which is what keeps the next person on a handed-down phone
 *     from seeing them.
 *
 * DELIBERATELY OFF IT:
 *   · `session/current` — carries the signed-in person's id, name and email. That is the
 *     definition of user-identifying, and this store is unencrypted and backed up. It also
 *     must not be believed on launch: whether a session is still valid is the service's
 *     answer, not a cached one.
 *   · `notes/detail`, `meta/minimum-version` — in the registry, but nothing produces either
 *     today. Pre-approving a key nobody writes yet would mean the first query to use it goes
 *     to disk without anyone deciding it should, which is the deny-list failure by a longer
 *     route.
 *   · every mutation — see `shouldDehydrateMutation` below.
 */
const domainAndAction = (key: readonly unknown[]) => `${String(key[0])}/${String(key[1])}`;

/**
 * `detail` and friends take an id; only segments 0 and 1 are read, so the argument is a
 * placeholder and never appears in the list. Written this way so the whitelist comes out of
 * the registry rather than being a second hand-spelled copy of it.
 */
const PERSISTED_ACTIONS: ReadonlySet<string> = new Set([
  domainAndAction(queryKeys.notes.list()),
]);

/**
 * TIMESTAMPS SURVIVE THE ROUND TRIP, which plain JSON does not give you.
 *
 * A model in this app holds real `Date` objects — the converter is where a wire string
 * becomes one. `JSON.stringify` writes a Date as an ISO string and `JSON.parse` hands back a
 * string, so a hydrated note would carry a `createdAt` that is typed `Date` and is not one.
 * The first render after a cold start then calls `.toLocaleString()` on a string and throws
 * into the error boundary — a blank screen, which is strictly worse than the empty list this
 * whole change exists to remove.
 *
 * WHY NOT A REVIVER THAT RECOGNISES TIMESTAMPS BY THEIR FIELD NAME. That is what this file
 * did until now: revive a string whose KEY ended in `At` and whose value looked like an
 * ISO-8601 instant. It was correct for the fields that existed on the day it was written and
 * for no other reason — nothing enforced the naming, no lint rule, no structure test. The
 * first `dueDate`, `publishedOn` or `expiry` anyone adds is silently NOT revived: it comes
 * back off disk as a string, typed `Date`, and the first `.toLocaleString()` on it is the
 * blank screen above. A rule the compiler cannot see, whose only failure mode is the exact
 * crash it was written to prevent, is not a rule worth keeping.
 *
 * SuperJSON removes the guess. It writes the SHAPE alongside the data — `{ json, meta }`,
 * where `meta.values` records the path of every value that is not plain JSON — so a `Date`
 * comes back a `Date` because the payload SAYS it is one, whatever the field is called. It
 * is the serializer the tRPC ecosystem defaults to, so the behaviour is well travelled.
 *
 * As a side effect the "field looks like a timestamp" hazard disappears in the other
 * direction too: a note whose BODY is an ISO string was written as a string and is read back
 * as a string, because nothing about it was ever a Date.
 *
 * This is not the converter rule being broken. A converter turns a WIRE shape into a model;
 * this puts a model back the way the converter already made it.
 */
export function serializeCache(client: PersistedClient): string {
  return SuperJSON.stringify(client);
}

/**
 * WHAT A BLOB THIS BUILD CANNOT READ TURNS INTO — and why it is not `undefined`.
 *
 * The stored shape changed with this file: what is on disk is `{ json, meta }` rather than
 * the client object itself. Across a RELEASE that is already handled and needs no migration
 * — `buster` below is the app version, so anything written by a different release is
 * discarded unread (see the note there). Within one version it can still happen on a
 * developer's phone, and reading yesterday's format must not be a crash.
 *
 * `persistQueryClientRestore` treats a restored client with no `timestamp` as unusable and
 * calls `removeClient()` on it, so handing it this constant DELETES the unreadable blob from
 * disk instead of leaving it there to be re-read and re-rejected on every launch. Returning
 * `undefined` would leave it in place, and the persister's own type does not allow it.
 */
const UNREADABLE: PersistedClient = {
  buster: "",
  timestamp: 0,
  clientState: { queries: [], mutations: [] },
};

/** A payload this serializer wrote. Anything else on that key came from another format. */
function isSuperJsonPayload(value: unknown): value is SuperJSONResult {
  return typeof value === "object" && value !== null && "json" in value;
}

/**
 * BOTH WAYS OUT OF HERE SAY SOMETHING, because this file already has an opinion about that:
 * `CACHE_WRITTEN_AT_KEY` above exists precisely so that a cache which vanishes is not
 * indistinguishable from one that was never written. A blob dropped for being unreadable is
 * the same class of event and earns the same treatment — and since it is deleted from disk
 * on the way out, each line is written at most once per occurrence rather than on every
 * launch for ever.
 *
 * The two are separated because they mean different things. A payload in another shape is a
 * format change meeting a phone that still holds yesterday's blob: expected, harmless, worth
 * one line. JSON that will not parse at all is CORRUPTION, and nothing in this file should be
 * able to produce it — an oversized payload is refused before it is written rather than
 * truncated — so that is the one somebody should look at.
 *
 * Neither line carries any of the blob. A size, and nothing else.
 */
export function deserializeCache(text: string): PersistedClient {
  let payload: unknown;

  try {
    payload = JSON.parse(text);
  } catch {
    log.warn("stored query cache would not parse, dropping it", { bytes: byteLength(text) });
    return UNREADABLE;
  }

  if (!isSuperJsonPayload(payload)) {
    log.info("stored query cache is in an older format, dropping it", {
      bytes: byteLength(text),
    });
    return UNREADABLE;
  }

  return SuperJSON.deserialize<PersistedClient>(payload);
}

export const queryPersister = createAsyncStoragePersister({
  storage: store,
  key: PERSISTED_CACHE_KEY,
  serialize: serializeCache,
  deserialize: deserializeCache,
});

export const persistOptions: PersistQueryClientProviderProps["persistOptions"] = {
  persister: queryPersister,
  maxAge: PERSISTED_MAX_AGE,
  /**
   * THE VERSION IS THE BUSTER, so a shipped shape change cannot rehydrate into a build that
   * no longer understands it — the restore is discarded and the app cold-starts once.
   *
   * A hand-maintained integer was the alternative and is worse in exactly one case, which is
   * the case that matters: the release where somebody renamed a field and forgot to bump it.
   * The version is already bumped by the release, by machinery, for other reasons.
   *
   * THE CHAIN, because this is what a change to the stored format leans on: Release Please
   * (`.github/workflows/release-please.yml`, `release-type: node`) bumps `package.json`;
   * `vite.config.ts` compiles that value in as `VITE_APP_VERSION`; `app/config/env.ts`
   * validates it and hands it over as `env.version`. No release can carry the previous
   * version, so no release can rehydrate the previous format — a change to what is written
   * here needs no migration, only a release.
   */
  buster: env.version,
  dehydrateOptions: {
    shouldDehydrateQuery: (query: Query) =>
      query.state.status === "success" && PERSISTED_ACTIONS.has(domainAndAction(query.queryKey)),
    /**
     * NO MUTATION IS EVER WRITTEN TO DISK, and the default here is genuinely dangerous.
     *
     * TanStack persists PAUSED mutations by default, so they can be replayed when the
     * network comes back — and a dehydrated mutation carries its `variables`. On this app the
     * mutation most likely to be paused is a sign-in attempted with no signal, whose
     * variables are an email address and a plaintext password. That would land in an
     * unencrypted, backed-up store.
     *
     * Offline replay is not a feature this app has, so nothing is lost by switching it off,
     * and turning it back on would need a per-mutation allow-list rather than a global flag.
     */
    shouldDehydrateMutation: () => false,
  },
};

/**
 * SIGN-OUT HAS TO REACH THE DISK COPY, not just the cache in memory.
 *
 * Dropping the in-memory queries is the visible half and it is not the half that leaks. The
 * blob on disk outlives the process, so without this the next person to sign in on the same
 * phone gets the previous user's notes painted onto their first screen — before any request
 * of theirs has even been sent. On a shared or handed-down device that is a data leak, not a
 * glitch, and it is invisible in any test that only looks at the query cache.
 *
 * Called from `forgetEverythingAboutThisUser` in shared/auth/auth.queries.ts, which is the
 * one place that decides what "this user is gone" means. Any save that the persister has
 * already scheduled and fires after this writes an empty payload, because by then the cache
 * holds nothing on the whitelist.
 */
export async function clearPersistedCache(): Promise<void> {
  await queryPersister.removeClient();
}

/**
 * The real size on disk, for a test or a measurement. Zero when nothing is stored.
 *
 * Reads the store directly rather than through `store.getItem`, which would count as the
 * cold-start read and report a loss that is really somebody taking a measurement.
 */
export async function persistedCacheBytes(): Promise<number> {
  const raw = await preferences.get(PERSISTED_CACHE_KEY);
  return raw ? byteLength(raw) : 0;
}
